import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import type { PlayerCri } from '../../types/neuro';
import { criLabel, criTone, formatMeta, isLimitedSample, statusLine, toneClasses } from '../../utils/neuro';
import { LimitedBadge, NeuroBar, PlayerAvatar, StatusIcon } from './NeuroPrimitives';
interface PlayerCriCardProps {
  player: PlayerCri;
  onOpen: (player: PlayerCri) => void;
}
export const PlayerCriCard = ({
  player,
  onOpen
}: PlayerCriCardProps) => {
  const tone = criTone(player.cri_match);
  const status = statusLine(player);
  const bars = [{
    label: 'Focus',
    value: player.fi_match,
    hint: null
  }, {
    label: 'Impulsivity',
    value: player.ii_match,
    hint: '↓ better'
  }, {
    label: 'Reaction',
    value: player.rei_match,
    hint: null
  }];
  return <div role="button" tabIndex={0} aria-label={`${player.name}, CRI ${player.cri_match} ${criLabel[tone]}. View report`} onClick={() => onOpen(player)} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(player);
    }
  }} className="group h-full flex flex-col rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm cursor-pointer transition-colors duration-150 ease-out hover:border-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-590" data-mp="94psnpcxozz3">
      {/* Header */}
      <div className="flex items-start gap-3" data-id="element-591" data-mp="1pmtrdjdk7txy">
        <PlayerAvatar player={player} size={40} data-id="element-592" data-mp="1pliodlrxuq7w" />
        <div className="min-w-0 flex-1" data-id="element-593" data-mp="1kptlp9kmgt7d">
          <div className="flex items-baseline gap-2" data-id="element-594" data-mp="1kmz4rs0thqip">
            <span className="text-sm font-semibold text-[#111827] truncate" data-id="element-595" data-mp="1fonr167eivv7">
              {player.name}
            </span>
            <span className="text-xs text-[#6B7280]" data-id="element-596" data-mp="1bnu6mmt63z72">{player.position}</span>
          </div>
          <div className="text-xs text-[#6B7280] mt-0.5" data-id="element-597" data-mp="4c3i03xf06y9">{formatMeta(player)}</div>
        </div>
        {isLimitedSample(player) && <LimitedBadge data-id="element-598" data-mp="bb8d9kms01g" />}
      </div>

      {/* CRI + bars */}
      <div className="mt-4 grid grid-cols-[auto,1fr] gap-5 items-center" data-id="element-599" data-mp="1rf8ua45urxn">
        <div className="pr-5 border-r border-[#E5E7EB]" data-id="element-600" data-mp="6pgsox9euva6">
          <div className={`text-[32px] leading-none font-semibold tabular-nums ${toneClasses[tone].text}`} data-id="element-601" data-mp="6n49yhvtwgw2">
            {player.cri_match}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-xs" data-id="element-602" data-mp="3svdqrprofcac">
            <span className={`w-2 h-2 rounded-full ${toneClasses[tone].dot}`} aria-hidden="true" data-id="element-603" data-mp="3f5a8tazty4qp" />
            <span className="font-medium text-[#111827]" data-id="element-604" data-mp="3jvgxfaucdp8y">CRI</span>
            <span className={toneClasses[tone].text} data-id="element-605" data-mp="3jxtocdi2v9y5">{criLabel[tone]}</span>
          </div>
        </div>
        <dl className="min-w-0 space-y-2" data-id="element-606" data-mp="354q6s64hkw7f">
          {bars.map(bar => <div key={bar.label} data-id="element-607" data-mp="3a6fdxaol8pq8">
              <div className="flex items-baseline justify-between gap-2" data-id="element-608" data-mp="2w64o8az0lh6n">
                <dt className="text-xs text-[#374151] truncate" data-id="element-609" data-mp="2w6xvre5ssapu">
                  {bar.label}
                  {bar.hint && <span className="ml-1 text-[11px] text-[#6B7280]" data-id="element-610" data-mp="2hj5zwovgf2w5">{bar.hint}</span>}
                </dt>
                <dd className="text-xs font-medium text-[#111827] tabular-nums" data-id="element-611" data-mp="2cl4e3w6n8pgz">
                  {bar.value}
                </dd>
              </div>
              <div className="mt-1" data-id="element-612" data-mp="2qhre67tw7vr4">
                <NeuroBar value={bar.value} data-id="element-613" data-mp="2lkj3stezkw0f" />
              </div>
            </div>)}
        </dl>
      </div>

      {/* Status line */}
      <div className={`mt-auto pt-4`} data-id="element-614" data-mp="30ggzkakzzyc4">
        <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${toneClasses[status.tone].bg} ${toneClasses[status.tone].text}`} data-id="element-615" data-mp="2vk0ane5z5fxz">
          <StatusIcon icon={status.icon} data-id="element-616" data-mp="3590ebavf8zii" />
          <span className="flex-1" data-id="element-617" data-mp="35abfd2xhft5x">{status.text}</span>
          <span className="inline-flex items-center gap-1 text-[#374151] opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 whitespace-nowrap" data-id="element-618" data-mp="3j8t14v3tuvl7">
            View report
            <ArrowRightIcon className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-619" data-mp="27889mktdv6j" />
          </span>
        </div>
      </div>
    </div>;
};
export default PlayerCriCard;